import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { CircularProgress, Box } from '@mui/material';
import { jwtDecode } from 'jwt-decode'; 

import Login from './Login';
import Dashboard from './Dashboard';
import SurveyManagement from './SurveyManagement';
import SurveyQuestions from './SurveyQuestions';
import Report from './Report';
import UserManagement from './UserManagement';
import TeamManagement from './TeamManagement';
import CohortManagement from './CohortManagement';
import Navigation from './Navigation';
import Footer from './Footer';
import HomePage from './HomePage';
import './App.css';
import Chatbot from './Chatbot';
import ForgotPassword from './ForgotPassword';
import Register from './Register';
import InviteCompletion from './InviteCompletion';
import Profile from './Profile';
import ResetPassword from './ResetPassword';
import EmailConfirm from './EmailConfirm';
import config from './config.js';
import ReportsLanding from './ReportsLanding';
import api from './api';

import SelfPerceptionInventorySurvey from './SelfPerceptionInventorySurvey';
import SelfPerceptionInventoryResults from './SelfPerceptionInventoryResults';
import ProfessionalReputationUserPage from './ProfessionalReputationUserPage';
import ProfessionalReputationSurvey from './ProfessionalReputationSurvey';
import CharismaDiagnosticSurvey from './CharismaDiagnosticSurvey';
import CharismaDiagnosticResults from './CharismaDiagnosticResults';
import BrandClarity from './BrandClarity'; // NEW: Import BrandClarity
import ImpersonationBanner from './ImpersonationBanner';
import AboutPage from './AboutPage'; // Import the new component

function LoadingIndicator() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <CircularProgress />
        </Box>
    );
}

function AppRoutes() {
    const [userId, setUserId] = useState(null);
    const [token, setToken] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [userCohortId, setUserCohortId] = useState(null);
    const [userTeamId, setUserTeamId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [isImpersonating, setIsImpersonating] = useState(false);
    const [impersonatorInfo, setImpersonatorInfo] = useState({ id: null, name: '' });

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const [openSurvey, setOpenSurvey] = useState(null);
    const [hasCompletedSurvey, setHasCompletedSurvey] = useState(false);
    const [spiIsVisible, setSpiIsVisible] = useState(false);
    const [spiIsCompleted, setSpiIsCompleted] = useState(false);
    const [spiCohortId, setSpiCohortId] = useState(null);
    const [prsIsVisible, setPrsIsVisible] = useState(false);
    const [prsIsConfigured, setPrsIsConfigured] = useState(false);
    const [prsUniqueToken, setPrsUniqueToken] = useState(null);
    const [prsIsAcceptingResponses, setPrsIsAcceptingResponses] = useState(false);
    const [prsCohortId, setPrsCohortId] = useState(null);
    const [charismaIsVisible, setCharismaIsVisible] = useState(false);
    const [charismaIsCompleted, setCharismaIsCompleted] = useState(false);
    const [charismaCohortId, setCharismaCohortId] = useState(null);
    const [brandIsVisible, setBrandIsVisible] = useState(false); // NEW: Brand visibility state

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => { document.title = "Radian"; }, []);

    const fetchNotifications = useCallback(async () => {
        if (!localStorage.getItem('token')) return; 
        try {
            const response = await api.get('/api/notifications');
            const fetchedNotifications = response.data || [];
            setNotifications(fetchedNotifications);
            setUnreadCount(fetchedNotifications.filter(n => !n.is_read).length);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    }, []);

    const syncAuthState = useCallback(() => {
        setIsLoading(true);
        const storedToken = localStorage.getItem("token");
        setToken(storedToken || null);
        const impersonatingFlag = localStorage.getItem('isImpersonating') === 'true';
        setIsImpersonating(impersonatingFlag);

        if (impersonatingFlag) {
            setImpersonatorInfo({ id: localStorage.getItem('original_admin_userId'), name: localStorage.getItem('original_admin_userName') });
        } else {
            setImpersonatorInfo({ id: null, name: '' });
        }

        if (storedToken) {
            try {
                const decodedToken = jwtDecode(storedToken);
                setUserId(decodedToken.user_id || null);
                setUserName(decodedToken.name || '');
                setUserRole(decodedToken.role_id ? decodedToken.role_id.toString() : null);
                setUserCohortId(decodedToken.cohort_id || null);
                setUserTeamId(decodedToken.team_id || null);
                
                localStorage.setItem('userId', decodedToken.user_id || '');
                localStorage.setItem('userName', decodedToken.name || '');
                localStorage.setItem('userRole', decodedToken.role_id ? decodedToken.role_id.toString() : '');
                localStorage.setItem('userCohortId', decodedToken.cohort_id != null ? decodedToken.cohort_id.toString() : '');
                localStorage.setItem('userTeamId', decodedToken.team_id != null ? decodedToken.team_id.toString() : '');

            } catch (error) {
                console.error("Token error in syncAuthState:", error);
                localStorage.clear();
                setToken(null); setUserId(null); setUserName(''); setUserRole(null); setUserCohortId(null); setUserTeamId(null);
                setIsImpersonating(false); setImpersonatorInfo({ id: null, name: '' });
                setNotifications([]); setUnreadCount(0);
            }
        } else {
            setUserId(null); setUserName(''); setUserRole(null); setUserCohortId(null); setUserTeamId(null);
            setIsImpersonating(false); setImpersonatorInfo({ id: null, name: '' });
            setNotifications([]); setUnreadCount(0);
        }
        setIsLoading(false);
    }, []); 

    useEffect(() => {
        syncAuthState();
        window.addEventListener('authChanged', syncAuthState);
        return () => window.removeEventListener('authChanged', syncAuthState);
    }, [syncAuthState]);

    const fetchUserProfile = useCallback(async () => {
        if (!userId || !token) return;
        try {
            const response = await api.get(`${config.backendUrl}/profile`); 
            const profileData = response.data;
            let derivedCohortId = userCohortId; 
            let derivedTeamId = userTeamId;
            if (profileData.team_id) {
                derivedTeamId = parseInt(profileData.team_id, 10);
                const teamResponse = await api.get(`${config.backendUrl}/teams/${profileData.team_id}`);
                if (teamResponse.data && teamResponse.data.cohort_id) {
                    derivedCohortId = parseInt(teamResponse.data.cohort_id, 10);
                }
            } else if (profileData.cohort_id) { 
                 derivedCohortId = parseInt(profileData.cohort_id, 10);
            }
            if (derivedCohortId !== null && derivedCohortId !== userCohortId) {
                setUserCohortId(derivedCohortId);
                localStorage.setItem("userCohortId", derivedCohortId.toString());
            }
            if (derivedTeamId !== null && derivedTeamId !== userTeamId) {
                setUserTeamId(derivedTeamId);
                localStorage.setItem("userTeamId", derivedTeamId.toString());
            }
        } catch (error) {
            console.error("App.js: Error fetching user profile:", error);
        }
    }, [userId, token, userCohortId, userTeamId]); 


    const fetchUserSurveyStatus = useCallback(async (surveyId) => {
        if (!userId || !token) return;
        try {
            const response = await api.get(`${config.backendUrl}/api/user-survey-status?survey_id=${surveyId}`);
            setHasCompletedSurvey(!!response.data.has_completed);
        } catch (error) {
            console.error("App.js: Error fetching user survey status:", error);
            setHasCompletedSurvey(false);
        }
    }, [userId, token]);

    const fetchOpenSurvey = useCallback(async () => {
        if (!token) return; 
        try {
            const response = await api.get(`${config.backendUrl}/api/open-survey`);
            const data = response.data;
            if (data.open_round && data.open_round.survey_id) { 
                setOpenSurvey({ id: data.open_round.survey_id, name: data.open_round.survey_name }); 
                fetchUserSurveyStatus(data.open_round.survey_id); 
            } else {
                setOpenSurvey(null);
            }
        } catch (error) {
            console.error("App.js: Error fetching open survey:", error);
            setOpenSurvey(null);
        }
    }, [token, fetchUserSurveyStatus]); 

    const fetchAssessmentStatuses = useCallback(async () => {
        if (!userId || !token) return;
        try {
            const response = await api.get(`${config.backendUrl}/api/assessment_status`); 
            const data = response.data;
            if (data.spi) { 
                setSpiIsVisible(!!data.spi.is_visible); setSpiIsCompleted(!!data.spi.is_completed); setSpiCohortId(data.spi.cohort_id); 
            } else { 
                setSpiIsVisible(false); setSpiIsCompleted(false); setSpiCohortId(null); 
            }
            if (data.prs) { 
                setPrsIsVisible(!!data.prs.is_visible); setPrsIsConfigured(!!data.prs.is_configured); 
                setPrsIsAcceptingResponses(!!data.prs.is_accepting); setPrsUniqueToken(data.prs.unique_survey_token || null); 
                setPrsCohortId(data.prs.cohort_id); 
            } else { 
                setPrsIsVisible(false); setPrsIsConfigured(false); setPrsIsAcceptingResponses(false); setPrsUniqueToken(null); setPrsCohortId(null); 
            }
            if (data.charisma) { 
                setCharismaIsVisible(!!data.charisma.is_visible); setCharismaIsCompleted(!!data.charisma.is_completed); 
                setCharismaCohortId(data.charisma.cohort_id); 
            } else { 
                setCharismaIsVisible(false); setCharismaIsCompleted(false); setCharismaCohortId(null); 
            }
            if (data.brand) { // NEW: Handle brand visibility
                setBrandIsVisible(!!data.brand.is_visible);
            } else {
                setBrandIsVisible(false);
            }
        } catch (error) {
            console.error("App.js: Error fetching assessment statuses:", error);
        }
    }, [userId, token]); 

     useEffect(() => {
        const initDataFetch = async () => {
            if (!isLoading && userId && token) { 
                await fetchUserProfile(); 
                fetchOpenSurvey(); 
                fetchAssessmentStatuses();
                fetchNotifications();
            } else if (!isLoading && !token) { 
                setOpenSurvey(null); setHasCompletedSurvey(false);
                setSpiIsVisible(false); setSpiIsCompleted(false); setSpiCohortId(null);
                setPrsIsVisible(false); setPrsIsConfigured(false); setPrsIsAcceptingResponses(false); setPrsUniqueToken(null); setPrsCohortId(null);
                setCharismaIsVisible(false); setCharismaIsCompleted(false); setCharismaCohortId(null);
                setBrandIsVisible(false); // NEW: Reset brand visibility on logout
                setNotifications([]); setUnreadCount(0);
            }
        };
        initDataFetch();
    }, [isLoading, userId, token, fetchOpenSurvey, fetchAssessmentStatuses, fetchUserProfile, fetchNotifications]);


    const handleSurveySubmit = () => { setHasCompletedSurvey(true); if(token) fetchOpenSurvey(); };
    const handleSpiComplete = () => { setSpiIsCompleted(true); if (token) { fetchAssessmentStatuses(); fetchNotifications(); }};
    const handlePrsConfigChange = useCallback(() => { 
        if (token) {
            fetchAssessmentStatuses(); 
            fetchNotifications(); 
        }
    }, [token, fetchAssessmentStatuses, fetchNotifications]); 

    const handleCharismaComplete = () => { setCharismaIsCompleted(true); if (token) { fetchAssessmentStatuses(); fetchNotifications(); }};

    const isLoggedIn = !!token && !!userId;

    const handleMarkNotificationsRead = async () => {
        if (unreadCount === 0) return;
        try {
            await api.post('/api/notifications/mark_read');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Failed to mark notifications as read:", error);
        }
    };

    const handleDeleteNotification = async (notificationId) => {
        const originalNotifications = notifications;
        const newNotifications = notifications.filter(n => n.notification_id !== notificationId);
        setNotifications(newNotifications);
        setUnreadCount(newNotifications.filter(n => !n.is_read).length);
        try {
            await api.delete(`/api/notifications/${notificationId}`);
        } catch (error) {
            console.error(`Failed to delete notification ${notificationId}:`, error);
            setNotifications(originalNotifications);
            setUnreadCount(originalNotifications.filter(n => !n.is_read).length);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const actualAdminUserIdForUserManagement = isImpersonating ? impersonatorInfo.id : userId;
    const actualAdminNameForUserManagement = isImpersonating ? impersonatorInfo.name : userName;

    return (
        <>
            <ImpersonationBanner 
                isImpersonating={isImpersonating} 
                impersonatorName={impersonatorInfo.name} 
                targetUserName={userName} 
            />
            <Navigation
                isLoggedIn={isLoggedIn}
                userRole={userRole} 
                userName={userName} 
                openSurvey={openSurvey}
                hasCompletedSurvey={hasCompletedSurvey}
                spiIsVisible={spiIsVisible}
                spiIsCompleted={spiIsCompleted}
                prsIsVisible={prsIsVisible} 
                charismaIsVisible={charismaIsVisible} 
                charismaIsCompleted={charismaIsCompleted}
                brandIsVisible={brandIsVisible} // NEW: Pass brand visibility to Navigation
                isImpersonating={isImpersonating} 
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkRead={handleMarkNotificationsRead}
                onDeleteNotification={handleDeleteNotification}
            />
            <div className={`main-content ${isImpersonating ? 'impersonating-active' : ''}`}>
                <Routes>
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/login" element={(!isLoggedIn || isImpersonating) ? <Login /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/register" element={!isLoggedIn ? <Register /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/forgot-password" element={!isLoggedIn ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/reset_password/:token" element={!isLoggedIn ? <ResetPassword /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/invite/:token" element={!isLoggedIn ? <InviteCompletion /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/emailconfirm/:token" element={!isLoggedIn ? <EmailConfirm /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/survey/prs/:uniqueSurveyToken" element={<ProfessionalReputationSurvey />} />

                    {/* NEW: Route for About/FAQ Page */}
                    <Route path="/about" element={<AboutPage />} />

                    <Route 
                        path="/dashboard" 
                        element={isLoggedIn ? 
                            <Dashboard 
                                openSurvey={openSurvey} hasCompletedSurvey={hasCompletedSurvey} userId={userId} userRole={userRole}
                                spiIsVisible={spiIsVisible} spiIsCompleted={spiIsCompleted} spiCohortId={spiCohortId}
                                prsIsVisible={prsIsVisible} prsIsConfigured={prsIsConfigured} prsUniqueToken={prsUniqueToken} prsIsAcceptingResponses={prsIsAcceptingResponses} prsCohortId={prsCohortId}
                                charismaIsVisible={charismaIsVisible} charismaIsCompleted={charismaIsCompleted} charismaCohortId={charismaCohortId}
                                brandIsVisible={brandIsVisible} // NEW: Pass brand visibility to Dashboard
                                userName={userName} // <-- ADD THIS LINE
                                userTeamId={userTeamId} // <-- ADD THIS LINE
                            /> : <Navigate to="/login" replace />} 
                    />
                    <Route path="/survey-management" element={isLoggedIn && (userRole === '1' || userRole === '2') ? <SurveyManagement /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/survey-rounds/:surveyId/questions" element={isLoggedIn ? <SurveyQuestions onSurveySubmit={handleSurveySubmit} /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/report/:surveyId/:userId" element={isLoggedIn ? <Report /> : <Navigate to="/dashboard" replace />} />
                     <Route 
                        path="/user-management" 
                        element={isLoggedIn && userRole === '1' && !isImpersonating ? 
                            <UserManagement 
                                currentAdminUserId={actualAdminUserIdForUserManagement} 
                                currentAdminName={actualAdminNameForUserManagement}   
                            /> : <Navigate to="/dashboard" replace />} 
                    />
                    <Route path="/team-management" element={isLoggedIn && (userRole === '1' || userRole === '2') ? <TeamManagement /> : <Navigate to="/dashboard" replace />} />
                    <Route path="/cohort-management" element={isLoggedIn && userRole === '1' && !isImpersonating ? <CohortManagement /> : <Navigate to="/dashboard" replace />} /> 
                    <Route path="/chatbot" element={isLoggedIn ? <Chatbot /> : <Navigate to="/login" replace />} />
                    <Route path="/profile" element={isLoggedIn ? <Profile /> : <Navigate to="/login" replace />} />
                    <Route path="/reports-landing" element={isLoggedIn ? <ReportsLanding userRole={userRole} userId={userId} /> : <Navigate to="/login" replace />} />
                    
                    <Route 
                        path="/assessment/spi/survey" 
                        element={isLoggedIn && spiIsVisible && !spiIsCompleted ? 
                            <SelfPerceptionInventorySurvey 
                                onSpiComplete={handleSpiComplete} 
                                cohortId={spiCohortId} 
                            /> 
                            : <Navigate to="/dashboard" replace />} 
                    />
                    <Route 
                        path="/assessment/spi/results" 
                        element={isLoggedIn && spiIsVisible && spiIsCompleted ? 
                            <SelfPerceptionInventoryResults 
                                cohortId={spiCohortId} 
                                currentUserId={userId} 
                                teamId={userTeamId} 
                            /> 
                            : <Navigate to="/dashboard" replace />} 
                    />
                    <Route 
                        path="/assessment/prs/config" 
                        element={
                            isLoggedIn && prsIsVisible ? 
                            <ProfessionalReputationUserPage 
                                onConfigChange={handlePrsConfigChange}
                                currentUserId={userId} 
                                currentUserCohortId={userCohortId} 
                            /> 
                            : <Navigate to="/dashboard" replace />
                        } 
                    />
                    <Route 
                        path="/assessment/charisma/survey" 
                        element={isLoggedIn && charismaIsVisible && !charismaIsCompleted ? 
                            <CharismaDiagnosticSurvey 
                                onCharismaComplete={handleCharismaComplete} 
                                cohortId={charismaCohortId} 
                            /> 
                            : <Navigate to="/dashboard" replace />} 
                    />
                    <Route 
                        path="/assessment/charisma/results" 
                        element={isLoggedIn && charismaIsVisible && charismaIsCompleted ? 
                            <CharismaDiagnosticResults 
                                cohortId={charismaCohortId} 
                            /> 
                            : <Navigate to="/dashboard" replace />} 
                    />
                    {/* NEW: Route for Brand Clarity Tool */}
                    <Route 
                        path="/brand-clarity"
                        element={isLoggedIn && brandIsVisible ? <BrandClarity /> : <Navigate to="/dashboard" replace />}
                    />


                    <Route path="/" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <Navigate to="/home" replace />} />
                    <Route path="*" element={<h1>404 - Page Not Found</h1>} />
                </Routes>
            </div>
            <Footer />
        </>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <BrowserRouter>
                <div className="App-wrapper"> 
                    <AppRoutes />
                </div>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
